/* eslint-disable no-nested-ternary */
import React, { useState, useCallback, useRef, useEffect, useContext } from 'react';
import { ThemeProvider } from 'styled-components';
import _ from 'lodash';
import { AutoCompleteProps } from './types';
import { getPropertyByPathString } from '../utils/arrays';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import { InputContainer } from '../Input/styles';
import Select from '../Select/Select';

const AutoComplete: React.FC<AutoCompleteProps> = (props) => {
  const {
    input,
    options = [],
    keyProperty = 'id',
    showProperty = 'name',
    dropDownOnly,
    readOnly = false,
    testId,
    isValueObject,
    overAll = false,
    onChange,
    onFocus,
    onBlur,
    minCharactersToSearch = 0,
  } = props;
  const inputRef: React.Ref<HTMLInputElement> = useRef(null);
  const [filteredOptions, setFilteredOptions] = useState([...options]);
  const [value, setValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState({
    index: 0,
    id: '',
  });
  const { actualTheme } = useContext(ThemeContext);

  const handleChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      if (options.length > 0 && newValue.length >= minCharactersToSearch) {
        if (newValue) {
          const newOptions = options.filter((option) =>
            _.get(option, showProperty).toLowerCase().includes(newValue.toLowerCase()),
          );
          setFilteredOptions(newOptions);
          setHighlightedIndex({
            index: 0,
            id: newOptions[0] && getPropertyByPathString(newOptions[0], keyProperty),
          });
        } else {
          setFilteredOptions(options);
        }
      }
    },
    [options, keyProperty, showProperty, minCharactersToSearch],
  );

  const handleBlur = useCallback(() => {
    const selectedOption = options.find((option) =>
      isValueObject
        ? _.get(option, keyProperty) === _.get(input.value, keyProperty)
        : _.get(option, keyProperty) === input.value,
    );
    if (selectedOption) {
      setValue(_.get(selectedOption, showProperty));
      handleChange(_.get(selectedOption, showProperty));
      setFilteredOptions(options);
    } else {
      setValue('');
      if (!minCharactersToSearch) {
        setFilteredOptions(options);
      }
    }
    if (input?.onBlur) input.onBlur();
    if (onBlur) onBlur();
  }, [
    options,
    input,
    onBlur,
    isValueObject,
    keyProperty,
    showProperty,
    handleChange,
    minCharactersToSearch,
  ]);

  const onSelectOption = useCallback(
    (valueChange: any) => {
      if (input?.onChange)
        input.onChange(isValueObject ? valueChange : _.get(valueChange, keyProperty));
      if (onChange) onChange(isValueObject ? valueChange : _.get(valueChange, keyProperty));
      setIsOpen(false);
      setValue(_.get(valueChange, showProperty));
    },
    [input, isValueObject, keyProperty, onChange, showProperty],
  );

  const handleFocus = useCallback(() => {
    if (!readOnly) {
      setIsOpen(true);
      setValue('');
    }
    if (onFocus) onFocus();
  }, [onFocus, readOnly]);

  const expand = useCallback(() => {
    if (isOpen) setIsOpen(false);
    else inputRef.current.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!minCharactersToSearch) setFilteredOptions([...options]);
  }, [options, minCharactersToSearch]);

  useEffect(() => {
    if (!input?.value) setValue('');
    else {
      const option = options.find((opt) =>
        isValueObject
          ? _.get(opt, keyProperty) === _.get(input.value, keyProperty)
          : _.get(opt, keyProperty) === input.value,
      );
      if (option) {
        setValue(_.get(option, showProperty));
      }
    }
  }, [options, showProperty, keyProperty, isValueObject, input.value]);

  const handleClear = useCallback(() => {
    if (input?.onChange) input.onChange('');
    if (onChange) onChange('');
    handleChange('');
  }, [handleChange, onChange, input]);

  return (
    <ThemeProvider theme={actualTheme}>
      <InputContainer id="autocomplete-id">
        <Select
          {...props}
          inputRef={inputRef}
          readOnly={readOnly || dropDownOnly}
          value={value}
          onChange={handleChange}
          onClear={handleClear}
          onBlur={handleBlur}
          onFocus={handleFocus}
          options={filteredOptions}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          onSelection={onSelectOption}
          setIsOpen={setIsOpen}
          isOpen={isOpen}
          showProperty={showProperty}
          keyProperty={keyProperty}
          expand={expand}
          testId={testId}
          highlightedSubString={value}
          pointer={false}
          overAll={overAll}
          minCharactersToSearch={minCharactersToSearch}
          showChevron={minCharactersToSearch === 0}
        />
      </InputContainer>
    </ThemeProvider>
  );
};

export default AutoComplete;
